// Roles tab: pick a role, edit its name/description, toggle the permissions it
// grants (affects everyone with the role), rename its key, or delete it. Plus a
// "New role" form. System roles (admin) can't be renamed or deleted.
import { useState } from 'react';
import { Operator, Role, PermissionDef, AdminOperator } from '../../types/database';
import { adminAccessService } from '../../services/adminAccess';
import PermissionMatrix from './PermissionMatrix';

interface RolesPanelProps {
  actor: Operator;
  roles: Role[];
  permissions: PermissionDef[];
  rolePermsByRole: Map<string, Set<string>>;
  operators: AdminOperator[];
  onChanged: () => Promise<void> | void;
  onError: (message: string) => void;
}

export default function RolesPanel({
  actor,
  roles,
  permissions,
  rolePermsByRole,
  operators,
  onChanged,
  onError,
}: RolesPanelProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newRole, setNewRole] = useState({ key: '', name: '', description: '' });
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const selected = roles.find((r) => r.key === selectedKey) ?? null;
  const memberCount = (key: string) => operators.filter((o) => o.role === key).length;

  function select(role: Role) {
    setSelectedKey(role.key);
    setEditName(role.name);
    setEditDesc(role.description ?? '');
    setCreating(false);
  }

  async function run(fn: () => Promise<unknown>, key?: string) {
    if (key) setBusyKey(key);
    try {
      await fn();
      await onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      if (key) setBusyKey(null);
    }
  }

  async function handleCreate() {
    const key = newRole.key.trim();
    const name = newRole.name.trim();
    if (!key || !name) {
      onError('New role needs a key and a name.');
      return;
    }
    try {
      await adminAccessService.createRole(actor, key, name, newRole.description.trim() || undefined);
      setNewRole({ key: '', name: '', description: '' });
      setCreating(false);
      await onChanged();
      setSelectedKey(key);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleSaveDetails() {
    if (!selected) return;
    await run(() => adminAccessService.updateRole(actor, selected.key, editName, editDesc.trim() || undefined));
  }

  async function handleRename() {
    if (!selected) return;
    const newKey = prompt(`Rename role key "${selected.key}" to:`, selected.key);
    if (!newKey || newKey === selected.key) return;
    try {
      await adminAccessService.renameRole(actor, selected.key, newKey.trim());
      await onChanged();
      setSelectedKey(newKey.trim());
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleDelete() {
    if (!selected) return;
    const count = memberCount(selected.key);
    let reassignTo: string | undefined;
    if (count > 0) {
      const options = roles.filter((r) => r.key !== selected.key).map((r) => r.key);
      reassignTo =
        prompt(
          `"${selected.name}" has ${count} account(s). Reassign them to which role key before deleting?\nOptions: ${options.join(', ')}`,
          options[0] ?? ''
        ) || undefined;
      if (!reassignTo) return;
    } else if (!confirm(`Delete role "${selected.name}"?`)) {
      return;
    }
    try {
      await adminAccessService.deleteRole(actor, selected.key, reassignTo);
      setSelectedKey(null);
      await onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  }

  const roleKeys = selected ? rolePermsByRole.get(selected.key) ?? new Set<string>() : new Set<string>();

  return (
    <div className="access-split">
      <div className="access-list">
        <div className="access-list-items">
          {roles.map((r) => (
            <button
              key={r.key}
              className={`access-list-item ${r.key === selectedKey ? 'selected' : ''}`}
              onClick={() => select(r)}
            >
              <span className="access-list-item-main">
                <span className="access-list-item-title">
                  {r.name} {r.is_system && <span className="access-badge access-badge--system">System</span>}
                </span>
                <span className="access-list-item-sub">{r.key}</span>
              </span>
              <span className="access-badge access-badge--count">{memberCount(r.key)}</span>
            </button>
          ))}
        </div>
        <button
          className="access-btn"
          style={{ width: '100%', borderRadius: 0, borderLeft: 0, borderRight: 0, borderBottom: 0 }}
          onClick={() => {
            setCreating(true);
            setSelectedKey(null);
          }}
        >
          + New role
        </button>
      </div>

      <div className="access-detail">
        {creating ? (
          <>
            <div className="access-detail-header">
              <h2>New role</h2>
            </div>
            <div className="access-field">
              <label htmlFor="new-role-key">Key (lowercase, a–z 0–9 _)</label>
              <input
                id="new-role-key"
                type="text"
                value={newRole.key}
                placeholder="e.g. supervisor"
                onChange={(e) => setNewRole({ ...newRole, key: e.target.value })}
              />
            </div>
            <div className="access-field">
              <label htmlFor="new-role-name">Name</label>
              <input
                id="new-role-name"
                type="text"
                value={newRole.name}
                placeholder="e.g. Supervisor"
                onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
              />
            </div>
            <div className="access-field">
              <label htmlFor="new-role-desc">Description (optional)</label>
              <input
                id="new-role-desc"
                type="text"
                value={newRole.description}
                onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
              />
            </div>
            <div className="access-row">
              <button className="access-btn access-btn--primary" onClick={handleCreate}>Create role</button>
              <button className="access-btn" onClick={() => setCreating(false)}>Cancel</button>
            </div>
            <div className="access-note">New roles start with no permissions — add them after creating.</div>
          </>
        ) : !selected ? (
          <div className="access-detail-empty">Select a role to edit its permissions, or create a new one.</div>
        ) : (
          <>
            <div className="access-detail-header">
              <div>
                <h2>
                  {selected.name}{' '}
                  {selected.is_system && <span className="access-badge access-badge--system">System</span>}
                </h2>
                <div className="access-detail-sub">
                  <span className="perm-row-key">{selected.key}</span> · {memberCount(selected.key)} account(s)
                </div>
              </div>
              <div className="access-row">
                {!selected.is_system && (
                  <button className="access-btn" onClick={handleRename}>Rename key</button>
                )}
                {!selected.is_system && (
                  <button className="access-btn access-btn--danger" onClick={handleDelete}>Delete</button>
                )}
              </div>
            </div>

            <div className="access-field">
              <label htmlFor="role-name">Display name</label>
              <input id="role-name" type="text" value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div className="access-field">
              <label htmlFor="role-desc">Description</label>
              <input id="role-desc" type="text" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
            </div>
            <div className="access-row" style={{ marginBottom: 'var(--spacing-md, 1rem)' }}>
              <button
                className="access-btn"
                disabled={editName.trim() === selected.name && (editDesc.trim() || '') === (selected.description ?? '')}
                onClick={handleSaveDetails}
              >
                Save name/description
              </button>
            </div>

            <div className="access-field" style={{ marginBottom: 0 }}>
              <label>Permissions granted by this role</label>
              <div className="access-note">Changes apply to everyone with this role on their next login.</div>
            </div>
            <PermissionMatrix
              mode="role"
              permissions={permissions}
              rolePermissionKeys={roleKeys}
              busyKey={busyKey}
              onToggleRole={(key, enabled) =>
                run(() => adminAccessService.setRolePermission(actor, selected.key, key, enabled), key)
              }
            />
          </>
        )}
      </div>
    </div>
  );
}
